import React, { useCallback, useMemo } from 'react';
import {
  cn,
  ToggleGroup,
  ToggleItem,
  ToolButton,
  TooltipTriggerIcon,
  ValueInputField,
} from '@crabs-ai/ds-react';
import { ColorPickerField } from '../components/color-picker-field';
import {
  FormatLetterSpacing2,
  Remove,
  RoundedCorner,
  VerticalAlignCenter,
  Visibility,
  VisibilityOffFill,
} from '@crabs-ai/web-icons';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import {
  TextItem,
  TextItemBackground,
  TextBackgroundMode,
  defaultTextItemBackground,
} from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { InspectorSubLabel } from '../components/inspector-label';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { useSelectionValues } from '../utils/use-selection-values';

type BackgroundControlsProps = {
  itemId: string;
  background?: TextItemBackground | null;
  isEyeOff?: boolean;
  onToggleEyeVisibility?: () => void;
  onDeactivate?: () => void;
};

const BackgroundControls: React.FC<BackgroundControlsProps> = ({
  itemId,
  background,
  isEyeOff = false,
  onToggleEyeVisibility,
  onDeactivate,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);

  const { items: backgroundItems } = useSelectionValues<
    TextItem | CaptionsItem,
    TextItemBackground | null | undefined
  >(itemId, (item) => item.background);

  const normalizedBackgrounds = useMemo(() => {
    if (backgroundItems.length === 0) {
      return background
        ? [
            {
              ...defaultTextItemBackground,
              ...background,
            },
          ]
        : [];
    }

    return backgroundItems.map((current) => {
      const currentBg = (current.background ??
        {}) as Partial<TextItemBackground>;
      return {
        ...defaultTextItemBackground,
        ...currentBg,
      };
    });
  }, [backgroundItems, background]);

  const backgroundValue = useMemo<TextItemBackground>(() => {
    if (normalizedBackgrounds[0]) {
      return {
        ...normalizedBackgrounds[0],
      };
    }

    return {
      ...defaultTextItemBackground,
      ...(background ?? {}),
    };
  }, [normalizedBackgrounds, background]);

  const isDefaultBackground = useMemo(() => {
    if (normalizedBackgrounds.length === 0) {
      return (
        defaultTextItemBackground.color === backgroundValue.color &&
        Math.abs(defaultTextItemBackground.opacity - backgroundValue.opacity) <
          0.0001 &&
        defaultTextItemBackground.visible === backgroundValue.visible &&
        defaultTextItemBackground.horizontalPadding ===
          backgroundValue.horizontalPadding &&
        defaultTextItemBackground.verticalPadding ===
          backgroundValue.verticalPadding &&
        defaultTextItemBackground.borderRadius ===
          backgroundValue.borderRadius &&
        defaultTextItemBackground.mode === backgroundValue.mode
      );
    }

    return normalizedBackgrounds.every((current) => {
      return (
        current.color === defaultTextItemBackground.color &&
        Math.abs(current.opacity - defaultTextItemBackground.opacity) <
          0.0001 &&
        current.visible === defaultTextItemBackground.visible &&
        current.horizontalPadding ===
          defaultTextItemBackground.horizontalPadding &&
        current.verticalPadding === defaultTextItemBackground.verticalPadding &&
        current.borderRadius === defaultTextItemBackground.borderRadius &&
        current.mode === defaultTextItemBackground.mode
      );
    });
  }, [normalizedBackgrounds, backgroundValue]);

  // Mixed 상태 감지
  const isColorMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.color ?? defaultTextItemBackground.color;
    return normalizedBackgrounds.some((current) => current.color !== first);
  }, [normalizedBackgrounds]);

  const isOpacityMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.opacity ?? defaultTextItemBackground.opacity;
    return normalizedBackgrounds.some(
      (current) => Math.abs(current.opacity - first) > 0.0001,
    );
  }, [normalizedBackgrounds]);

  const isBorderRadiusMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.borderRadius ??
      defaultTextItemBackground.borderRadius;
    return normalizedBackgrounds.some(
      (current) => current.borderRadius !== first,
    );
  }, [normalizedBackgrounds]);

  const isHorizontalPaddingMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.horizontalPadding ??
      defaultTextItemBackground.horizontalPadding;
    return normalizedBackgrounds.some(
      (current) => current.horizontalPadding !== first,
    );
  }, [normalizedBackgrounds]);

  const isVerticalPaddingMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.verticalPadding ??
      defaultTextItemBackground.verticalPadding;
    return normalizedBackgrounds.some(
      (current) => current.verticalPadding !== first,
    );
  }, [normalizedBackgrounds]);

  const isModeMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.mode ?? defaultTextItemBackground.mode;
    return normalizedBackgrounds.some((current) => current.mode !== first);
  }, [normalizedBackgrounds]);

  const isVerticalOffsetMixed = useMemo(() => {
    if (normalizedBackgrounds.length <= 1) return false;
    const first =
      normalizedBackgrounds[0]?.verticalOffset ??
      defaultTextItemBackground.verticalOffset;
    return normalizedBackgrounds.some(
      (current) => current.verticalOffset !== first,
    );
  }, [normalizedBackgrounds]);

  const updateBackground = useCallback(
    (
      updater: (current: TextItemBackground) => TextItemBackground,
      commitToUndoStack: boolean,
    ) => {
      setState({
        update: (state) =>
          changeItemsWithSelection(state, itemId, targetIds, (item) => {
            if (item.type !== 'text' && item.type !== 'captions') {
              return item;
            }

            const currentBg: TextItemBackground = {
              ...defaultTextItemBackground,
              ...((item as TextItem).background ?? {}),
            };
            const nextBg = updater(currentBg);

            return {
              ...(item as TextItem),
              background: { ...nextBg },
            };
          }),
        commitToUndoStack,
      });
    },
    [itemId, setState, targetIds],
  );

  const handleColorChange = useCallback(
    (color: string, commitToUndoStack = true) => {
      const normalized =
        color === 'transparent'
          ? color
          : color.startsWith('#')
            ? color
            : `#${color}`;
      updateBackground(
        (current) => ({
          ...current,
          color: normalized,
        }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleOpacityChange = useCallback(
    (newOpacity: number, commitToUndoStack = true) => {
      updateBackground(
        (current) => ({ ...current, opacity: newOpacity }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleBorderRadiusChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      const clamped = Math.max(0, num);
      updateBackground(
        (current) => ({
          ...current,
          borderRadius: clamped,
        }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleHorizontalPaddingChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      const clamped = Math.max(0, num);
      updateBackground(
        (current) => ({
          ...current,
          horizontalPadding: clamped,
        }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleVerticalPaddingChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      const clamped = Math.max(0, num);
      updateBackground(
        (current) => ({
          ...current,
          verticalPadding: clamped,
        }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleVerticalOffsetChange = useCallback<NumberControlUpdateHandler>(
    ({ num, commitToUndoStack }) => {
      // -50 ~ 50 범위로 제한
      const clamped = Math.min(50, Math.max(-50, num));
      updateBackground(
        (current) => ({
          ...current,
          verticalOffset: clamped,
        }),
        commitToUndoStack,
      );
    },
    [updateBackground],
  );

  const handleToggleVisible = useCallback(() => {
    onToggleEyeVisibility?.();
  }, [onToggleEyeVisibility]);

  const handleModeChange = useCallback(
    (mode: TextBackgroundMode) => {
      updateBackground(
        (current) => ({
          ...current,
          mode,
        }),
        true,
      );
    },
    [updateBackground],
  );

  const handleReset = useCallback(() => {
    updateBackground(() => ({ ...defaultTextItemBackground }), true);
    onDeactivate?.();
  }, [updateBackground, onDeactivate]);

  return (
    <div className="flex flex-col gap-3xs">
      {/* 상단: 컬러, 투명도, visible, 해제 */}
      <div className="flex items-center gap-4xs">
        <div
          className={cn(
            'flex flex-1 items-center gap-4xs',
            isEyeOff && 'pointer-events-none opacity-40',
          )}
        >
          <ColorPickerField
            value={backgroundValue.color}
            onChange={(color) => handleColorChange(color, false)}
            onChangeEnd={(color) => handleColorChange(color, true)}
            accessibilityLabel="Background color"
            hasMixedValue={isColorMixed}
            opacity={backgroundValue.opacity}
            onOpacityChange={(newOpacity) =>
              handleOpacityChange(newOpacity, false)
            }
            onOpacityChangeEnd={(newOpacity) =>
              handleOpacityChange(newOpacity, true)
            }
            isOpacityMixed={isOpacityMixed}
            className="flex flex-1 items-center gap-4xs"
          />
        </div>
        <ToolButton
          aria-label="Toggle background visibility"
          onClick={handleToggleVisible}
          isShowTooltip
          tooltipNode={!isEyeOff ? '속성 끄기' : '속성 켜기'}
          state={isEyeOff ? 'selected' : 'default'}
          isTransparent={false}
          className="h-[36px] w-[36px]"
        >
          {!isEyeOff ? (
            <Visibility className="size-4" />
          ) : (
            <VisibilityOffFill className="size-4" />
          )}
        </ToolButton>
        <ToolButton
          aria-label="Reset background"
          onClick={handleReset}
          disabled={isDefaultBackground}
          isShowTooltip
          tooltipNode="속성 삭제"
          className={cn(
            'h-[36px] w-[36px] text-sys-cont-neutral-default',
            isDefaultBackground && 'opacity-40',
          )}
        >
          <Remove className="size-4" />
        </ToolButton>
      </div>

      {/* 배경 영역 선택 */}
      <div className="flex flex-col gap-2xs">
        <InspectorSubLabel>
          <div className="flex items-center gap-2xs">
            {t('Editor.inspector.text-background.mode')}
            <TooltipTriggerIcon
              tooltipText={t('Editor.inspector.text-background.mode-tooltip')}
              tooltipProps={{ side: 'top', sideOffset: 4 }}
            />
            {isModeMixed && (
              <span className="text-comp-accent-xsmall"> (Mixed)</span>
            )}
          </div>
        </InspectorSubLabel>
        <ToggleGroup
          type="single"
          value={isModeMixed ? '' : backgroundValue.mode}
          onValueChange={(value) => {
            if (value && typeof value === 'string') {
              handleModeChange(value as TextBackgroundMode);
            }
          }}
          disabled={isEyeOff}
          className="w-fit"
        >
          <ToggleItem
            value="text-only"
            aria-label="Text only background"
            label={t('Editor.inspector.text-background.mode-text-only')}
            showIcon={false}
          />
          <ToggleItem
            value="full-area"
            aria-label="Full area background"
            label={t('Editor.inspector.text-background.mode-full-area')}
            showIcon={false}
          />
        </ToggleGroup>
      </div>

      {/* 곡률, 위치 미세조정 */}
      <div className="flex items-center gap-4xs">
        <div className="flex min-w-0 flex-1 flex-col gap-2xs">
          <InspectorSubLabel>
            {t('Editor.inspector.text-background.border-radius')}
          </InspectorSubLabel>
          <ValueInputField
            isUnit="px"
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <RoundedCorner className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
            }
            setValue={handleBorderRadiusChange}
            value={backgroundValue.borderRadius}
            min={0}
            max={undefined}
            step={1}
            accessibilityLabel="Border radius"
            hasMixedValue={isBorderRadiusMixed}
            disabled={isEyeOff}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2xs">
          <InspectorSubLabel>
            <div className="flex items-center gap-2xs">
              {t('Editor.inspector.text-background.vertical-offset')}
              <TooltipTriggerIcon
                tooltipText={t(
                  'Editor.inspector.text-background.vertical-offset-tooltip',
                )}
                tooltipProps={{ side: 'top', sideOffset: 4 }}
              />
            </div>
          </InspectorSubLabel>
          <ValueInputField
            isUnit="%"
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <VerticalAlignCenter className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
            }
            setValue={handleVerticalOffsetChange}
            value={backgroundValue.verticalOffset}
            min={-50}
            max={50}
            step={1}
            accessibilityLabel="Vertical offset"
            hasMixedValue={isVerticalOffsetMixed}
            disabled={isEyeOff || backgroundValue.mode === 'full-area'}
          />
        </div>
      </div>

      {/* 좌우 여백, 상하 여백 */}
      <div className="flex items-center gap-4xs">
        <div className="flex min-w-0 flex-1 flex-col gap-2xs">
          <InspectorSubLabel>
            {t('Editor.inspector.text-background.horizontal-padding')}
          </InspectorSubLabel>
          <ValueInputField
            isUnit="px"
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <FormatLetterSpacing2 className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
            }
            setValue={handleHorizontalPaddingChange}
            value={backgroundValue.horizontalPadding}
            min={0}
            max={undefined}
            step={1}
            accessibilityLabel="Horizontal padding"
            hasMixedValue={isHorizontalPaddingMixed}
            disabled={isEyeOff}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2xs">
          <InspectorSubLabel>
            {t('Editor.inspector.text-background.vertical-padding')}
          </InspectorSubLabel>
          <ValueInputField
            isUnit="px"
            isShowLeadingNode={true}
            leadingNodeType="icon"
            leadingNodeValue={
              <FormatLetterSpacing2 className="size-[20px] rotate-90 [&_path]:fill-sys-cont-neutral-default" />
            }
            setValue={handleVerticalPaddingChange}
            value={backgroundValue.verticalPadding}
            min={0}
            max={undefined}
            step={1}
            accessibilityLabel="Vertical padding"
            hasMixedValue={isVerticalPaddingMixed}
            disabled={isEyeOff}
          />
        </div>
      </div>
    </div>
  );
};

export default BackgroundControls;
